<template>
    <div class="form-wrapper">
        <h3 class="title-contact-form">{{ title }}</h3>
        <p class="text-contact-form">{{ text }}</p>

        <alert v-if="alert.type" :type="alert.type" :message="alert.message"></alert>

        <form class="form-contact" action="#" @submit.prevent="handleSubmit">
            <select v-model="fields.subject" name="subject" class="select">
                <option disabled selected value="">{{ trans.form.subject[''] }}</option>
                <option v-for="(value, key) in trans.form.subject" :value="key" v-if="key !== ''">{{ value }}</option>
            </select>

            <input v-model="fields.first_name" name="first_name" type="text" :placeholder="trans.form.first_name" />
            <input v-model="fields.last_name" name="last_name" type="text" :placeholder="trans.form.last_name" />
            <input v-model="fields.email" name="email" type="email" :placeholder="trans.form.email" />

            <select v-model="fields.country" name="country" :title="trans.form.country">
                <option disabled selected value="">{{ trans.form.country }}</option>
                <option v-for="(value, key) in trans.form.listCountries" :value="key">{{ value }}</option>
            </select>

            <div class="phone-wrapper">
                <select v-model="fields.code" name="code" :title="trans.form.code" @change="checkCountryCode">
                    <option disabled selected value="">{{ trans.form.code }}</option>
                    <option v-for="(value, key) in trans.form.listPhoneCodes" :value="key">{{ value }}</option>
                </select>
                <input v-model="fields.phone" name="phone" type="text" :placeholder="trans.form.phone" @input="formatPhoneNumber" maxlength="15" />
            </div>
            
            <p v-if="phoneError" class="error-message">{{ phoneError }}</p>

            <select v-model="fields.preferred_contact_method" name="preferred_contact_method" :title="trans.form.preferred_contact_method['']">
                <option disabled selected value="">{{ trans.form.preferred_contact_method[''] }}</option>
                <option v-for="(value, key) in trans.form.preferred_contact_method" :value="key" v-if="key !== ''">{{ value }}</option>
            </select>

            <textarea v-model="fields.message" name="message" :placeholder="trans.form.message" rows="8"></textarea>

            <div class="recaptcha-wrapper">
                <vue-recaptcha
                    ref="recaptcha"
                    :loadRecaptchaScript="true"
                    :sitekey="recaptchaSiteKey"
                    @verify="onRecaptchaVerify"
                    @expired="onRecaptchaExpired"
                />
            </div>

            <button class="button button-big button-block" type="submit">{{ trans.buttons.send }}</button>
        </form>
    </div>
</template>

<style lang="scss">
    .form-wrapper .title-contact-form {
        font-size: 20px;
        color: var(--color-camarote-primary, #641467);
    }
    .form-wrapper .text-contact-form {
        font-size: 16px;
    }
    .phone-wrapper {
        display: flex;
        gap: 10px;
    }
    .error-message {
        color: red !important;
        font-size: 15px !important;
        margin-top: 10px;
        margin-bottom: 0px !important;
    }
    .recaptcha-wrapper > div {
        display: flex;
        justify-content: center;
        align-items: center;
        margin-top: 20px;
    }
    .form-contact textarea {
        width: 100%;
        min-height: 180px;
        resize: vertical;
    }
</style>

<script>
    import axios from 'axios'
    import Alert from './AlertForm.vue'
    import VueRecaptcha from 'vue-recaptcha'

    export default {
        props: ['title', 'text', 'trans'],
        components: { Alert, VueRecaptcha },
        data() {
            return {
                alert: {
                    type: null,
                    message: null,
                },
                fields: {
                    subject: '',
                    first_name: '',
                    last_name: '',
                    email: '',
                    country: '',
                    code: '',
                    phone: '',
                    preferred_contact_method: '',
                    message: '',
                    url: window.location.hostname + window.location.pathname
                },
                phoneError: '',
                recaptchaSiteKey: window.recaptchaSiteKey || '',
                recaptchaToken: null
            }
        },

        methods: {
            handleSubmit() {
                const { subject, first_name, last_name, email, country, code, phone, preferred_contact_method, message } = this.fields;

                if (!subject || !first_name || !last_name || !email || !country || !code || !phone || !preferred_contact_method || !message) {
                    this.showAlert(this.trans.form.messages.required, 'error');
                    return;
                }

                if (!this.recaptchaToken) {
                    this.showAlert(this.trans.recaptcha, 'error');
                    return;
                }

                const rawPhone = this.fields.phone.replace(/\D/g, '');
                const countryCode = this.trans.form.listPhoneCodes[code].replace(/[^\d+]/g, '');

                if (countryCode && countryCode === "+55" && rawPhone.length !== 11) {
                    this.phoneError = 'O número de telefone deve conter exatamente 11 dígitos para números brasileiros.';
                    setTimeout(() => {
                        this.phoneError = '';
                    }, 3000);
                    return;
                } else {
                    this.phoneError = '';
                }

                const cleanPhone = this.fields.phone.replace(/^(\+55)?/, '');
                const payload = {
                    ...this.fields,
                    phone: `${countryCode ? countryCode : ''}${cleanPhone}`,
                    'g-recaptcha-response': this.recaptchaToken
                }

                const apiURL = '/api/contact';
                axios.get(apiURL, { params: payload })
                    .then(response => {
                        this.resetFormData();
                        this.showAlert(response.data.message, 'success');
                        this.resetRecaptcha();
                    })
                    .catch(error => {
                        console.error(error);
                        this.showAlert(this.trans.form.messages.error, 'error');
                        this.resetRecaptcha();
                    });
            },

            onRecaptchaVerify(token) {
                this.recaptchaToken = token;
            },

            onRecaptchaExpired() {
                this.recaptchaToken = null;
            },

            resetRecaptcha() {
                this.recaptchaToken = null;
                if (this.$refs.recaptcha) {
                    this.$refs.recaptcha.reset();
                }
            },

            resetFormData() {
                Object.keys(this.fields).forEach(field => this.fields[field] = '');
                this.resetRecaptcha();
            },

            showAlert(message = '', type = null) {
                this.alert = { type, message };
            },

            formatPhoneNumber() {
                let phone = this.fields.phone.replace(/\D/g, '');

                if (phone.length === 0) {
                    this.fields.phone = '';
                    return;
                }

                const countryCode = this.trans.form.listPhoneCodes[this.fields.code].replace(/[^\d+]/g, '');
                if (countryCode && countryCode.includes("+55")) {
                    if (phone.length <= 2) {
                        phone = `(${phone}`;
                    } else if (phone.length <= 7) {
                        phone = `(${phone.slice(0, 2)}) ${phone.slice(2)}`;
                    } else {
                        phone = `(${phone.slice(0, 2)}) ${phone.slice(2, 7)}-${phone.slice(7, 11)}`;
                    }
                }

                this.fields.phone = phone;
            },

            checkCountryCode() {
                const countryCode = this.trans.form.listPhoneCodes[this.fields.code].replace(/[^\d+]/g, '');
                if (countryCode && countryCode !== this.trans.form.listPhoneCodes[this.fields.code]) {
                    this.fields.phone = '';
                }
            }
        }
    }
</script>
